import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { ReloadOutlined } from '@ant-design/icons';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';

import MainCard from 'components/MainCard';
import PageHeader from 'components/PageHeader';
import DayOpeningActions from 'components/dayOpen/DayOpeningActions';
import TableLoadedFooter from 'components/table/TableLoadedFooter';
import TableStatusChip from 'components/table/TableStatusChip';
import useDayOpening from 'hooks/useDayOpening';
import { brand, infoAlertSx } from 'utils/designTokens';
import { formatDayDateTime, getDayUserLabel, isDayOpen } from 'utils/dayOpening';

function formatDate(val) {
  if (!val) return '—';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return String(val);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const COLS = [
  { label: '#', align: 'center' },
  { label: 'Date', align: 'left' },
  { label: 'Start time', align: 'left' },
  { label: 'Close time', align: 'left' },
  { label: 'Opened by', align: 'left' },
  { label: 'Closed by', align: 'left' },
  { label: 'Status', align: 'center' }
];

export default function DayOpenList() {
  const {
    rows,
    loading,
    acting,
    confirmAction,
    setConfirmAction,
    openDay,
    canStart,
    canClose,
    canRestart,
    fetchList,
    runAction,
    handleActionClick,
    confirmCopy
  } = useDayOpening();

  return (
    <Box>
      <PageHeader
        title="Day Open"
        subtitle="Start, close, or restart the business day."
        count={rows.length}
        titleExtra={
          openDay ? (
            <Chip
              label="Day open"
              size="small"
              variant="outlined"
              sx={{
                fontWeight: 600,
                bgcolor: brand.overlay,
                color: brand.primary,
                borderColor: brand.borderFocus
              }}
            />
          ) : (
            <Chip label="Day closed" size="small" variant="outlined" sx={{ fontWeight: 600 }} />
          )
        }
        action={
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Tooltip title="Refresh">
              <IconButton onClick={() => fetchList()} size="small" disabled={loading || acting}>
                <ReloadOutlined />
              </IconButton>
            </Tooltip>
            <DayOpeningActions
              variant="page"
              acting={acting}
              canStart={canStart}
              canClose={canClose}
              canRestart={canRestart}
              onStart={() => handleActionClick('start')}
              onClose={() => handleActionClick('close')}
              onRestart={() => handleActionClick('restart')}
              confirmAction={confirmAction}
              confirmCopy={confirmCopy}
              onConfirmClose={() => { if (!acting) setConfirmAction(null); }}
              onConfirm={() => runAction(confirmAction)}
              onCancelConfirm={() => setConfirmAction(null)}
            />
          </Stack>
        }
      />

      {openDay && (
        <Alert severity="info" sx={{ ...infoAlertSx, mb: 2 }}>
          Current day opened on <strong>{formatDayDateTime(openDay.start_time)}</strong>
          {openDay.OpenedBy ? (
            <>
              {' '}
              by <strong>{getDayUserLabel(openDay.OpenedBy)}</strong>
            </>
          ) : null}
          .
        </Alert>
      )}

      <MainCard contentSX={{ p: 0 }}>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            display: 'block',
            maxHeight: { xs: '55vh', md: 520 },
            overflowX: 'auto',
            overflowY: 'auto',
            borderBottom: 'none',
            '& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root': {
              borderBottom: 'none'
            }
          }}
        >
          <Table stickyHeader sx={{ minWidth: 880 }}>
            <TableHead>
              <TableRow>
                {COLS.map((col) => (
                  <TableCell
                    key={col.label}
                    align={col.align}
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.72rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      bgcolor: 'grey.50',
                      whiteSpace: 'nowrap',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                      py: 1.5
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={COLS.length} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={36} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLS.length} align="center" sx={{ py: 8 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No day opening records yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Start the business day to begin operations.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={acting === 'start' ? <CircularProgress size={16} color="inherit" /> : <PlayArrowRoundedIcon />}
                      disabled={!!acting}
                      onClick={() => handleActionClick('start')}
                      sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none' }}
                    >
                      Start day
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, idx) => {
                  const open = isDayOpen(row);
                  return (
                    <TableRow
                      key={row.id ?? idx}
                      hover
                      sx={open ? { bgcolor: `${brand.overlay}` } : undefined}
                    >
                      <TableCell align="center" sx={{ color: 'text.disabled', width: 48 }}>
                        {idx + 1}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDate(row.date)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" color="text.secondary">
                          {formatDayDateTime(row.start_time)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" color="text.secondary">
                          {formatDayDateTime(row.close_time)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{getDayUserLabel(row.OpenedBy)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{getDayUserLabel(row.ClosedBy)}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <TableStatusChip
                          active={open}
                          label={open ? 'Open' : 'Closed'}
                          sx={{ minWidth: 88 }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
              {!loading && rows.length > 0 && (
                <TableLoadedFooter count={rows.length} singular="record" colSpan={COLS.length} />
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </MainCard>
    </Box>
  );
}
